import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { DepreciationPreset, TemplateParamDefinition, TemplateVersion } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Dialog } from '../../components/ui/Dialog';
import { FormField, FormSection } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/Textarea';
import { isConflictError, serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { PercentInput } from './PercentInput';
import {
  DAMAGE_GROUP_SIZE,
  feeSections,
  fromPercentInput,
  groupParams,
  isFractionUnit,
  issuesByKey,
  PARAM_GROUP_ORDER,
  toPercentDisplay,
  type ValueIssue,
  valueIssuesOf,
} from './templates-display';

/** The keys a starter preset writes: four depreciation years, six grade deductions, two damage tables. */
const PRESET_PARAM_KEYS = ['depYear1', 'depYear2', 'depYear3', 'depYear4', 'gradeG7', 'gradeG4', 'gradeG3', 'gradeG2', 'gradeG1', 'gradeG0'];

const PRESET_REPLACED_COUNT = PRESET_PARAM_KEYS.length + 2 * DAMAGE_GROUP_SIZE;

const toDisplay = (value: number | undefined, definition: TemplateParamDefinition): string => {
  if (value === undefined || !Number.isFinite(value)) {
    return '';
  }

  return String(isFractionUnit(definition.unit) ? toPercentDisplay(value) : value);
};

/**
 * `/templates/:id/draft` — the whole value set of one draft version, on its own page (plan §7).
 *
 * Sixty numbers do not belong in a sheet, so they are laid out in the catalogue's own groups. Rates are
 * edited as percentages and stored as fractions; the two Arabic damage tables keep their original
 * wording with an English gloss. A starter preset **only pre-fills rates** — the depreciation years, the
 * grade deductions and the damage tables — behind a confirmation that says how many values it replaces
 * (plan §3.2).
 *
 * Saving carries `expectedRevision`: a second editor's save turns into a translated 409 and a refetch,
 * never a silent overwrite (plan §2.3). Values the server rejects come back keyed, and each message is
 * printed under the field it belongs to.
 */
export const TemplateValuesForm = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();

  const templateQuery = useQuery(orpc.templates.get.queryOptions({ input: { templateId: id }, enabled: id !== '' }));
  const catalogQuery = useQuery(orpc.templates.paramCatalog.queryOptions());
  // The workspace's own presets (plan §11.19): an administrator may have edited the factory values.
  const presetsQuery = useQuery(orpc.templates.depreciationPresets.queryOptions());
  const presets: readonly DepreciationPreset[] = presetsQuery.data ?? [];

  const template = templateQuery.data ?? null;
  const draft: TemplateVersion | null = template?.versions.find((version) => version.status === 'DRAFT') ?? null;
  const catalog = catalogQuery.data ?? null;

  const [loadedVersionId, setLoadedVersionId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [damage1, setDamage1] = useState<string[]>([]);
  const [damage2, setDamage2] = useState<string[]>([]);
  const [overridable, setOverridable] = useState<string[]>([]);
  const [referencePrice, setReferencePrice] = useState('');
  const [referenceSource, setReferenceSource] = useState('');
  const [referenceDate, setReferenceDate] = useState('');
  const [reason, setReason] = useState('');
  const [presetKey, setPresetKey] = useState('');
  const [isPresetOpen, setIsPresetOpen] = useState(false);
  const [issues, setIssues] = useState<ValueIssue[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  // Load the draft into the form exactly once per version, so a save never clobbers what is on screen.
  useEffect(() => {
    if (!draft || !catalog || loadedVersionId === draft.id) {
      return;
    }

    setLoadedVersionId(draft.id);
    setValues(Object.fromEntries(catalog.params.map((definition) => [definition.key, toDisplay(draft.params[definition.key], definition)])));
    setDamage1(draft.damageGroup1.map(String));
    setDamage2(draft.damageGroup2.map(String));
    setOverridable([...draft.overridableKeys]);
    setReferencePrice(draft.referencePriceDefault === null ? '' : String(draft.referencePriceDefault));
    setReferenceSource(draft.referenceSource ?? '');
    setReferenceDate(draft.referenceDate ?? '');
    setReason(draft.reason ?? '');
  }, [catalog, draft, loadedVersionId]);

  const saveDraft = useMutation(orpc.templates.updateDraftVersion.mutationOptions());

  const byKey = useMemo(() => issuesByKey(issues), [issues]);
  const grouped = useMemo(() => groupParams(catalog?.params ?? []), [catalog]);

  const applyPreset = (key: string): void => {
    const preset = presets.find((entry) => entry.key === key);

    if (!preset) {
      return;
    }

    const params: Record<string, number> = {
      ...Object.fromEntries(preset.rates.map((rate, index) => [`depYear${index + 1}`, rate])),
      ...Object.fromEntries(Object.entries(preset.gradeRates).map(([grade, rate]) => [`grade${grade}`, rate])),
    };
    const definitions = new Map((catalog?.params ?? []).map((definition) => [definition.key, definition]));

    setValues((current) => {
      const next = { ...current };

      for (const paramKey of PRESET_PARAM_KEYS) {
        const definition = definitions.get(paramKey);

        if (definition) {
          next[paramKey] = toDisplay(params[paramKey], definition);
        }
      }

      return next;
    });
    setDamage1(preset.damageGroup1.map(String));
    setDamage2(preset.damageGroup2.map(String));
  };

  const parseNumber = (raw: string): number | null => {
    const trimmed = raw.trim();

    if (trimmed === '') {
      return null;
    }

    const parsed = Number(trimmed);

    return Number.isFinite(parsed) ? parsed : null;
  };

  const onSave = async (): Promise<void> => {
    if (!draft || !catalog) {
      return;
    }

    setFormError(null);

    const localIssues: ValueIssue[] = [];
    const params: Record<string, number> = {};

    for (const definition of catalog.params) {
      const parsed = parseNumber(values[definition.key] ?? '');

      if (parsed === null) {
        localIssues.push({ key: definition.key, code: 'MISSING', detail: t('templates.valueRequired') });

        continue;
      }

      params[definition.key] = isFractionUnit(definition.unit) ? fromPercentInput(parsed) : parsed;
    }

    const toTable = (table: readonly string[], key: string): number[] =>
      table.map((entry, index) => {
        const parsed = parseNumber(entry);

        if (parsed === null) {
          localIssues.push({ key: `${key}.${index}`, code: 'MISSING', detail: t('templates.valueRequired') });

          return 0;
        }

        return parsed;
      });

    const damageGroup1 = toTable(damage1, 'damageGroup1');
    const damageGroup2 = toTable(damage2, 'damageGroup2');
    const price = parseNumber(referencePrice);

    if (localIssues.length > 0) {
      setIssues(localIssues);
      setFormError(t('templates.fixValues', { count: localIssues.length }));

      return;
    }

    try {
      await saveDraft.mutateAsync({
        versionId: draft.id,
        expectedRevision: draft.revision,
        referencePriceDefault: price,
        referenceSource: referenceSource.trim() === '' ? null : referenceSource.trim(),
        referenceDate: referenceDate.trim() === '' ? null : referenceDate.trim(),
        params,
        damageGroup1,
        damageGroup2,
        overridableKeys: overridable,
        reason,
      });

      setIssues([]);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.templates.get.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.templates.list.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.templates.previewVersion.key() }),
      ]);
      navigate(`/templates/${id}`);
    } catch (error: unknown) {
      if (isConflictError(error)) {
        setFormError(t('errors.staleRevision'));
        setLoadedVersionId(null);
        await templateQuery.refetch();

        return;
      }

      const serverIssues = valueIssuesOf(error);

      setIssues(serverIssues);
      setFormError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  };

  if (templateQuery.isLoading || catalogQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  if (templateQuery.isError || !template || !catalog) {
    return <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{t('errors.loadFailed')}</p>;
  }

  if (!draft) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/templates/${id}`)}
        >
          <ArrowLeft className="size-3.5" />
          {t('templates.backToTemplate')}
        </Button>
        <p className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">{t('templates.noDraft')}</p>
      </div>
    );
  }

  const fieldError = (key: string): string | undefined => byKey.get(key)?.[0]?.detail;

  const paramField = (definition: TemplateParamDefinition) => {
    const label = t(`params.${definition.key}`, definition.label);
    const error = fieldError(definition.key);

    return (
      <FormField
        key={definition.key}
        id={`param-${definition.key}`}
        label={label}
        hint={definition.provisional ? t('templates.provisionalHint') : undefined}
        error={error}
        required
      >
        {isFractionUnit(definition.unit) ? (
          <PercentInput
            id={`param-${definition.key}`}
            value={values[definition.key] ?? ''}
            hasError={Boolean(error)}
            onValueChange={(next) => setValues((current) => ({ ...current, [definition.key]: next }))}
          />
        ) : (
          <Input
            id={`param-${definition.key}`}
            type="number"
            dir="ltr"
            step={definition.integer ? 1 : 'any'}
            className="text-start tabular-nums"
            value={values[definition.key] ?? ''}
            hasError={Boolean(error)}
            onChange={(event) => setValues((current) => ({ ...current, [definition.key]: event.target.value }))}
          />
        )}
      </FormField>
    );
  };

  const damageSection = (
    key: 'damageGroup1' | 'damageGroup2',
    table: readonly string[],
    setTable: (next: string[]) => void,
    options: typeof catalog.damageGroup1,
  ) => (
    <FormSection title={t(`templates.${key}`)}>
      <p className="text-xs text-muted-foreground">{t('templates.damageGroupHint')}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {options.map((option) => (
          <FormField
            key={`${key}-${option.index}`}
            id={`${key}-${option.index}`}
            label={option.arabic}
            hint={option.english}
            error={fieldError(`${key}.${option.index}`)}
          >
            <Input
              id={`${key}-${option.index}`}
              type="number"
              dir="ltr"
              min={0}
              step="any"
              className="text-start tabular-nums"
              value={table[option.index] ?? ''}
              hasError={Boolean(fieldError(`${key}.${option.index}`))}
              onChange={(event) => {
                const next = [...table];

                next[option.index] = event.target.value;
                setTable(next);
              }}
            />
          </FormField>
        ))}
      </div>
    </FormSection>
  );

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/templates/${id}`)}
      >
        <ArrowLeft className="size-3.5" />
        {t('templates.backToTemplate')}
      </Button>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('templates.draftTitle', { name: template.name })}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('templates.draftSubtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="info">{t('templates.versionBadge', { version: draft.version })}</Badge>
          <Badge mono>{t('templates.revisionBadge', { revision: draft.revision })}</Badge>
        </div>
      </header>

      {formError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {formError}
        </p>
      ) : null}

      <div className="space-y-6 rounded-lg border border-border bg-card p-5">
        <FormSection title={t('templates.referenceSection')}>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              id="reference-price"
              label={t('templates.referencePriceDefault')}
              hint={t('templates.referencePriceHint')}
              error={fieldError('referencePriceDefault')}
            >
              <Input
                id="reference-price"
                type="number"
                dir="ltr"
                min={0}
                step="any"
                className="text-start tabular-nums"
                value={referencePrice}
                hasError={Boolean(fieldError('referencePriceDefault'))}
                onChange={(event) => setReferencePrice(event.target.value)}
              />
            </FormField>
            <FormField
              id="reference-source"
              label={t('templates.referenceSource')}
            >
              <Input
                id="reference-source"
                value={referenceSource}
                onChange={(event) => setReferenceSource(event.target.value)}
              />
            </FormField>
            <FormField
              id="reference-date"
              label={t('templates.referenceDate')}
            >
              <Input
                id="reference-date"
                type="date"
                dir="ltr"
                className="text-start"
                value={referenceDate}
                onChange={(event) => setReferenceDate(event.target.value)}
              />
            </FormField>
          </div>
        </FormSection>

        <FormSection title={t('templates.presetSection')}>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <FormField
                id="apply-preset"
                label={t('templates.applyPreset')}
                hint={t('templates.applyPresetHint')}
              >
                <Select
                  id="apply-preset"
                  value={presetKey}
                  placeholder={t('templates.presetNone')}
                  onChange={(event) => setPresetKey(event.target.value)}
                  options={presets.map((preset) => ({ value: preset.key, label: t(`templates.presets.${preset.key}`, preset.label) }))}
                />
              </FormField>
            </div>
            <Button
              variant="outline"
              disabled={presetKey === ''}
              onClick={() => setIsPresetOpen(true)}
            >
              <Wand2 className="size-4" />
              {t('templates.applyPresetAction')}
            </Button>
          </div>
        </FormSection>

        {PARAM_GROUP_ORDER.map((group) => {
          const definitions = grouped.get(group) ?? [];

          if (definitions.length === 0) {
            return null;
          }

          if (group === 'FEES') {
            // One field per row, in sections by what the money is for (business review, plan §11.19).
            return (
              <FormSection
                key={group}
                title={t(`paramGroups.${group}`)}
              >
                <div className="max-w-xl space-y-5">
                  {feeSections(definitions).map((section) => (
                    <fieldset
                      key={section.key}
                      className="space-y-3 border-s-2 border-border ps-4"
                    >
                      <legend className="mb-2 text-sm font-semibold text-card-foreground">{t(`templates.feeSections.${section.key}`)}</legend>
                      {section.definitions.map(paramField)}
                    </fieldset>
                  ))}
                </div>
              </FormSection>
            );
          }

          return (
            <FormSection
              key={group}
              title={t(`paramGroups.${group}`)}
            >
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{definitions.map(paramField)}</div>
            </FormSection>
          );
        })}

        {damageSection('damageGroup1', damage1, setDamage1, catalog.damageGroup1)}
        {damageSection('damageGroup2', damage2, setDamage2, catalog.damageGroup2)}

        <FormSection title={t('templates.overridableSection')}>
          <p className="text-xs text-muted-foreground">{t('templates.overridableHint')}</p>
          {fieldError('overridableKeys') ? <p className="text-xs text-destructive">{fieldError('overridableKeys')}</p> : null}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {catalog.params.map((definition) => (
              <Checkbox
                key={`override-${definition.key}`}
                id={`override-${definition.key}`}
                label={t(`params.${definition.key}`, definition.label)}
                checked={overridable.includes(definition.key)}
                onChange={(event) =>
                  setOverridable((current) =>
                    event.target.checked ? [...current, definition.key] : current.filter((entry) => entry !== definition.key),
                  )
                }
              />
            ))}
          </div>
        </FormSection>

        <FormSection title={t('templates.reason')}>
          <FormField
            id="draft-reason"
            label={t('templates.reason')}
            hint={t('templates.reasonHint')}
          >
            <Textarea
              id="draft-reason"
              rows={3}
              value={reason}
              placeholder={t('templates.reasonPlaceholder')}
              onChange={(event) => setReason(event.target.value)}
            />
          </FormField>
        </FormSection>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            isLoading={saveDraft.isPending}
            onClick={() => void onSave()}
          >
            {saveDraft.isPending ? t('actions.saving') : t('templates.saveDraft')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/templates/${id}`)}
          >
            {t('actions.cancel')}
          </Button>
        </div>
      </div>

      <Dialog
        isOpen={isPresetOpen}
        onClose={() => setIsPresetOpen(false)}
        title={t('templates.applyPresetTitle')}
        description={t('templates.applyPresetBody', {
          preset: presetKey === '' ? '' : t(`templates.presets.${presetKey}`),
          count: PRESET_REPLACED_COUNT,
        })}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsPresetOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (presetKey !== '') {
                  applyPreset(presetKey);
                }

                setIsPresetOpen(false);
              }}
            >
              {t('templates.applyPresetConfirm')}
            </Button>
          </>
        }
      />
    </div>
  );
};
